import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {
    PeopleAlt,
    CheckCircle,
    Logout,
    Visibility,
    Search,
    Download,
} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Navbar from "../components/Homepage/Navbar/Navbar";
import Footer from "../components/Homepage/Footer/Footer";

// Local development API URL
// For VPS deployment, use: https://rukap.edu.in/leave-api
// const API_URL = "http://127.0.0.1:5000";

const API_URL = "https://rukap.edu.in/leave-api";

export default function AdminDashboard() {
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [teachingSearch, setTeachingSearch] = useState("");
    const [nonTeachingSearch, setNonTeachingSearch] = useState("");
    const [applicationSearch, setApplicationSearch] = useState("");

    let user = {};

    try {
        user = JSON.parse(sessionStorage.getItem("leaveUser") || "{}");
    } catch {
        user = {};
    }

    const role = String(user.role || "").toUpperCase();

    const roleTitles = {
        ADMIN: "Administrator Dashboard",
        REGISTRAR: "Registrar Dashboard",
        PRINCIPAL: "Principal Dashboard",
        HOD: "Head of Department Dashboard",
    };

    const dashboardTitle =
        roleTitles[role] || "Leave Management Dashboard";

    const isRegistrarOrAdmin =
        role === "REGISTRAR" || role === "ADMIN";

    const handleLogout = () => {
        sessionStorage.removeItem("leaveToken");
        sessionStorage.removeItem("leaveUser");
        navigate("/leave-portal", { replace: true });
    };

    // Load employees and leave applications.
    useEffect(() => {
        let active = true;

        const loadDashboard = async () => {
            const token = sessionStorage.getItem("leaveToken");

            if (!token) {
                setError("Please log in to view the dashboard.");
                setLoading(false);
                return;
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            try {
                const [employeeResponse, applicationResponse] =
                    await Promise.all([
                        axios.get(`${API_URL}/api/admin/employees`, config),
                        axios.get(`${API_URL}/api/admin/leaves`, config),
                    ]);

                if (!active) return;

                setEmployees(employeeResponse.data.employees || []);

                const rows = applicationResponse.data.applications || [];

                setApplications(
                    rows.map((application) => {
                        let dates = application.dates;

                        if (!Array.isArray(dates)) {
                            dates = application.leave_dates
                                ? application.leave_dates.split(",")
                                : [];
                        }

                        return {
                            ...application,
                            dates: dates
                                .map((date) => String(date).trim())
                                .filter(Boolean),
                        };
                    })
                );
            } catch (err) {
                if (!active) return;

                if (err.response?.status === 401) {
                    setError("Your session has expired. Please log in again.");
                } else if (err.response?.status === 403) {
                    setError(
                        "You do not have permission to access this dashboard."
                    );
                } else {
                    setError(
                        err.response?.data?.message ||
                            "Unable to load dashboard data. Check whether the backend server is running."
                    );
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        loadDashboard();

        return () => {
            active = false;
        };
    }, []);

    // Format dates as DD-MM-YYYY.
    const formatDate = (value) => {
        const dateString = String(value || "").slice(0, 10);
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);

        return match
            ? `${match[3]}-${match[2]}-${match[1]}`
            : dateString || "-";
    };

    const formatSubmittedDate = (value) => {
        if (!value) return "-";

        const date = new Date(value);

        return Number.isNaN(date.getTime())
            ? "-"
            : date.toLocaleDateString("en-IN", {
                  timeZone: "Asia/Kolkata",
              });
    };

    // Current date and time in Indian Standard Time.
    const getCurrentDateTime = () =>
        new Date().toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        }) + " IST";

    // Teaching employee sorting.
    const getDesignationPriority = (designation = "") => {
        const value = String(designation).toLowerCase().trim();

        if (value.includes("vice chancellor")) return 1;

        if (
            value === "registrar" ||
            value.startsWith("registrar ")
        ) {
            return 2;
        }

        if (/\bprofessor\b/.test(value)) return 3;

        return 4;
    };

    const teachingEmployees = employees
        .filter((employee) => employee.employee_type === "TEACHING")
        .sort((a, b) => {
            const priorityDifference =
                getDesignationPriority(a.designation) -
                getDesignationPriority(b.designation);

            if (priorityDifference !== 0) return priorityDifference;

            const departmentDifference = String(
                a.department_name || ""
            ).localeCompare(String(b.department_name || ""), "en", {
                sensitivity: "base",
            });

            if (departmentDifference !== 0) return departmentDifference;

            const designationDifference = String(
                a.designation || ""
            ).localeCompare(String(b.designation || ""), "en", {
                sensitivity: "base",
            });

            if (designationDifference !== 0) return designationDifference;

            return String(a.employee_name || "").localeCompare(
                String(b.employee_name || ""),
                "en",
                { sensitivity: "base" }
            );
        });

    const nonTeachingEmployees = employees.filter(
        (employee) => employee.employee_type === "NON_TEACHING"
    );

    // Search employees.
    const filterEmployees = (list, search) => {
        const query = search.trim().toLowerCase();

        if (!query) return list;

        return list.filter((employee) =>
            [
                employee.employee_code,
                employee.employee_name,
                employee.designation,
                employee.department_name,
                employee.college_name,
            ].some((value) =>
                String(value || "").toLowerCase().includes(query)
            )
        );
    };

    const filteredTeachingEmployees = filterEmployees(
        teachingEmployees,
        teachingSearch
    );

    const filteredNonTeachingEmployees = filterEmployees(
        nonTeachingEmployees,
        nonTeachingSearch
    );

    // Search leave applications.
    const filteredApplications = applications.filter((application) => {
        const query = applicationSearch.trim().toLowerCase();

        if (!query) return true;

        return [
            application.application_no,
            application.employee_name,
            application.employee_code,
            application.employee_type,
            application.leave_type,
            application.status,
            application.purpose,
            ...(application.dates || []),
        ].some((value) =>
            String(value || "").toLowerCase().includes(query)
        );
    });

    // Role-based visibility for leave applications.
    const visibleApplications = applications.filter((application) => {
        if (role === "HOD") {
            const departmentId = user.departmentId ?? user.department_id;

            return (
                String(application.employee_type || "").toUpperCase() ===
                    "TEACHING" &&
                departmentId != null &&
                application.department_id != null &&
                String(application.department_id) === String(departmentId)
            );
        }

        if (role === "PRINCIPAL") {
            const collegeId = user.collegeId ?? user.college_id;

            return (
                String(application.employee_type || "").toUpperCase() ===
                    "TEACHING" &&
                collegeId != null &&
                application.college_id != null &&
                String(application.college_id) === String(collegeId)
            );
        }

        return role === "REGISTRAR" || role === "ADMIN";
    });

    const visibleFilteredApplications = visibleApplications.filter(
        (application) => {
            const search = String(applicationSearch || "").toLowerCase();

            return [
                application.application_no,
                application.employee_name,
                application.employee_code,
                application.department_name,
                application.purpose,
                application.leave_type,
                application.status,
            ].some((value) =>
                String(value || "").toLowerCase().includes(search)
            );
        }
    );

    // Today's date in Indian Standard Time.
    const getTodayIST = () => {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: "Asia/Kolkata",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(new Date());

        const values = Object.fromEntries(
            parts.map((part) => [part.type, part.value])
        );

        return `${values.year}-${values.month}-${values.day}`;
    };

    const todayIST = getTodayIST();

    // Approved applications that include today's date.
    const employeesOnLeaveToday = visibleApplications
        .filter((application) => {
            const dates = Array.isArray(application.dates)
                ? application.dates
                : [];

            const isApproved =
                String(application.status || "")
                    .trim()
                    .toUpperCase() === "APPROVED";

            const isOnLeaveToday = dates.some(
                (date) => String(date).trim().slice(0, 10) === todayIST
            );

            return isApproved && isOnLeaveToday;
        })
        .sort((a, b) => {
            const typeA =
                String(a.employee_type || "").toUpperCase() === "TEACHING"
                    ? 0
                    : 1;

            const typeB =
                String(b.employee_type || "").toUpperCase() === "TEACHING"
                    ? 0
                    : 1;

            if (typeA !== typeB) return typeA - typeB;

            return String(a.employee_name || "").localeCompare(
                String(b.employee_name || ""),
                "en",
                { sensitivity: "base" }
            );
        });

    const todayTeachingEmployees = employeesOnLeaveToday.filter(
        (application) =>
            String(application.employee_type || "").toUpperCase() ===
            "TEACHING"
    );

    const todayNonTeachingEmployees = employeesOnLeaveToday.filter(
        (application) =>
            String(application.employee_type || "").toUpperCase() ===
            "NON_TEACHING"
    );

    // Download today's leave report as PDF.
    const downloadTodayLeavePDF = () => {
        try {
            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
            });

            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);

            doc.text("RAYALASEEMA UNIVERSITY", 148.5, 15, {
                align: "center",
            });

            doc.setFontSize(12);
            doc.text("Employees on Leave Today", 148.5, 23, {
                align: "center",
            });

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);

            doc.text(`Date: ${todayIST}`, 14, 32);
            doc.text(
                `Total Employees on Leave: ${employeesOnLeaveToday.length}`,
                14,
                38
            );
            doc.text(
                `Teaching: ${todayTeachingEmployees.length} | Non-Teaching: ${todayNonTeachingEmployees.length}`,
                14,
                44
            );

            const rows = employeesOnLeaveToday.map(
                (application, index) => [
                    index + 1,
                    application.employee_code || "-",
                    application.employee_name || "-",
                    application.designation || "-",
                    application.department_name || "-",
                    application.college_name || "-",
                    application.leave_type || "-",
                    application.purpose || "-",
                    (Array.isArray(application.dates)
                        ? application.dates
                        : []
                    )
                        .map((date) => String(date).trim().slice(0, 10))
                        .join(", "),
                    application.total_days ?? "-",
                    application.status || "-",
                ]
            );

            autoTable(doc, {
                startY: 49,
                head: [[
                    "S.No.",
                    "Employee Code",
                    "Employee Name",
                    "Designation",
                    "Department",
                    "College",
                    "Leave Type",
                    "Purpose",
                    "Leave Dates",
                    "Days",
                    "Status",
                ]],
                body:
                    rows.length > 0
                        ? rows
                        : [[
                              "-",
                              "No employees are on approved leave today.",
                              "-",
                              "-",
                              "-",
                              "-",
                              "-",
                              "-",
                              "-",
                              "-",
                              "-",
                          ]],
                theme: "grid",
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                    overflow: "linebreak",
                    valign: "middle",
                },
                headStyles: {
                    fillColor: [11, 60, 93],
                    textColor: 255,
                    fontStyle: "bold",
                },
                columnStyles: {
                    0: { cellWidth: 10 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 29 },
                    3: { cellWidth: 24 },
                    4: { cellWidth: 28 },
                    5: { cellWidth: 24 },
                    6: { cellWidth: 18 },
                    7: { cellWidth: 35 },
                    8: { cellWidth: 28 },
                    9: { cellWidth: 12 },
                    10: { cellWidth: 16 },
                },
                margin: { left: 10, right: 10, bottom: 15 },
                didDrawPage: () => {
                    const pageCount = doc.getNumberOfPages();

                    doc.setFontSize(8);
                    doc.text(
                        `Rayalaseema University | Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
                        148.5,
                        202,
                        { align: "center" }
                    );
                },
            });

            doc.save(`Employees_On_Leave_${todayIST}.pdf`);
        } catch (error) {
            console.error("PDF download error:", error);
            window.alert(
                "PDF download failed. Please check the browser console for details."
            );
        }
    };

    // Dashboard statistics.
    const approvedApplications = applications.filter(
        (application) =>
            String(application.status || "").toUpperCase() === "APPROVED"
    );

    const totalApprovedLeaveDays = approvedApplications.reduce(
        (sum, application) =>
            sum + Number(application.total_days || 0),
        0
    );

    // IMPORTANT: Do not clamp the available balance to zero.
    // A balance can be negative, for example 20 - 21 = -1.
    const totalAvailableLeaveDays = employees.reduce(
        (sum, employee) => {
            const entitlement = Number(
                employee.annual_leave_entitlement || 0
            );
            const taken = Number(employee.leave_taken || 0);

            return sum + (entitlement - taken);
        },
        0
    );

    const stats = [
        {
            title: "Employees",
            value: employees.length,
            icon: <PeopleAlt />,
            color: "#1565C0",
        },
        {
            title: "Approved Applications",
            value: approvedApplications.length,
            icon: <CheckCircle />,
            color: "#2E7D32",
        },
        {
            title: "Approved Leave Days",
            value: totalApprovedLeaveDays,
            icon: <CheckCircle />,
            color: "#6A1B9A",
        },
        {
            title: "Available Leave Days",
            value: totalAvailableLeaveDays,
            icon: <PeopleAlt />,
            color: "#00838F",
        },
    ];

    // PDF employee table headings.
    const employeeHeaders = [
        "Employee Code",
        "Employee Name",
        "Employee Type",
        "Designation",
        "Department",
        "College",
        "Entitlement",
        "Taken",
        "Available",
    ];

    // PDF rows also retain negative available balances.
    const employeeRows = (list) =>
        list.map((employee) => {
            const entitlement = Number(
                employee.annual_leave_entitlement || 0
            );
            const taken = Number(employee.leave_taken || 0);

            return [
                employee.employee_code || "-",
                employee.employee_name || "-",
                employee.employee_type || "-",
                employee.designation || "-",
                employee.department_name || "-",
                employee.college_name || "-",
                entitlement,
                taken,
                entitlement - taken,
            ];
        });

    const applicationHeaders = [
        "Application No.",
        "Employee Name",
        "Employee Type",
        "Leave Type",
        "Days",
        "Selected Dates",
        "Purpose",
        "Status",
        "Submitted On",
    ];

    const applicationRows = (list) =>
        list.map((application) => [
            application.application_no || "-",
            application.employee_name ||
                String(application.employee_id || "-"),
            application.employee_type || "-",
            application.leave_type || "-",
            Number(application.total_days || 0),
            (application.dates || []).map(formatDate).join(", ") || "-",
            application.purpose || "-",
            application.status || "-",
            formatSubmittedDate(application.submitted_at),
        ]);

    // Add IST timestamp and page number to every PDF page.
    const addPdfFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();

        for (let page = 1; page <= pageCount; page++) {
            doc.setPage(page);

            const pageHeight = doc.internal.pageSize.getHeight();
            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFontSize(8);
            doc.text(
                `Generated on: ${getCurrentDateTime()}`,
                10,
                pageHeight - 7
            );
            doc.text(
                `Page ${page} of ${pageCount}`,
                pageWidth - 10,
                pageHeight - 7,
                { align: "right" }
            );
        }
    };

    // Export an individual table as PDF.
    const exportTablePDF = (title, headers, rows, filename) => {
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "mm",
            format: "a4",
        });

        doc.setFontSize(16);
        doc.text("Rayalaseema University", 14, 15);

        doc.setFontSize(12);
        doc.text(title, 14, 23);

        autoTable(doc, {
            startY: 29,
            head: [headers],
            body:
                rows.length > 0
                    ? rows
                    : [
                          headers.map((_, index) =>
                              index === 0 ? "No records found" : ""
                          ),
                      ],
            theme: "grid",
            styles: {
                fontSize: 7,
                cellPadding: 2,
                overflow: "linebreak",
                valign: "middle",
            },
            headStyles: {
                fillColor: [11, 60, 93],
                textColor: 255,
            },
            margin: {
                left: 10,
                right: 10,
                top: 12,
                bottom: 15,
            },
        });

        addPdfFooter(doc);
        doc.save(filename);
    };

    // Export all authorized tables into one PDF.
    const downloadAllTablesPDF = () => {
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "mm",
            format: "a4",
        });

        doc.setFontSize(20);
        doc.text("Rayalaseema University", 14, 22);

        doc.setFontSize(15);
        doc.text("Leave Management Report", 14, 34);

        doc.setFontSize(10);
        doc.text(`Report role: ${role || "Unknown"}`, 14, 44);
        doc.text(`Generated on: ${getCurrentDateTime()}`, 14, 51);
        doc.text(`Total employees: ${employees.length}`, 14, 60);
        doc.text(
            `Teaching employees: ${teachingEmployees.length}`,
            14,
            67
        );

        if (isRegistrarOrAdmin) {
            doc.text(
                `Non-teaching employees: ${nonTeachingEmployees.length}`,
                14,
                74
            );
        }

        doc.text(
            `Leave applications: ${applications.length}`,
            14,
            isRegistrarOrAdmin ? 81 : 74
        );

        const addSection = (title, headers, rows) => {
            doc.addPage();

            doc.setFontSize(13);
            doc.text(title, 14, 15);

            autoTable(doc, {
                startY: 21,
                head: [headers],
                body:
                    rows.length > 0
                        ? rows
                        : [
                              headers.map((_, index) =>
                                  index === 0 ? "No records found" : ""
                              ),
                          ],
                theme: "grid",
                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    overflow: "linebreak",
                    valign: "middle",
                },
                headStyles: {
                    fillColor: [11, 60, 93],
                    textColor: 255,
                },
                margin: {
                    left: 10,
                    right: 10,
                    top: 12,
                    bottom: 15,
                },
            });
        };

        addSection(
            "Teaching Employees",
            employeeHeaders,
            employeeRows(teachingEmployees)
        );

        if (isRegistrarOrAdmin) {
            addSection(
                "Non-Teaching Employees",
                employeeHeaders,
                employeeRows(nonTeachingEmployees)
            );
        }

        addSection(
            "Leave Applications",
            applicationHeaders,
            applicationRows(applications)
        );

        addPdfFooter(doc);
        doc.save("Rayalaseema_University_All_Tables.pdf");
    };

    // Reusable employee table.
    const renderEmployeeTable = (employeeList) => (
        <TableContainer
            component={Paper}
            sx={{ mb: 4, borderRadius: 2, overflowX: "auto" }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#F1F5F9" }}>
                        <TableCell>Employee Code</TableCell>
                        <TableCell>Employee Name</TableCell>
                        <TableCell>Designation</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>College</TableCell>
                        <TableCell align="right">Entitlement</TableCell>
                        <TableCell align="right">Taken</TableCell>
                        <TableCell align="right">Available</TableCell>
                        <TableCell>Action</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {employeeList.map((employee) => {
                        const entitlement = Number(
                            employee.annual_leave_entitlement || 0
                        );
                        const taken = Number(employee.leave_taken || 0);

                        // Keep negative balances instead of displaying zero.
                        const available = entitlement - taken;

                        return (
                            <TableRow key={employee.employee_id} hover>
                                <TableCell>
                                    {employee.employee_code || "-"}
                                </TableCell>
                                <TableCell>
                                    {employee.employee_name || "-"}
                                </TableCell>
                                <TableCell>
                                    {employee.designation || "-"}
                                </TableCell>
                                <TableCell>
                                    {employee.department_name || "-"}
                                </TableCell>
                                <TableCell>
                                    {employee.college_name || "-"}
                                </TableCell>
                                <TableCell align="right">
                                    {entitlement}
                                </TableCell>
                                <TableCell align="right">
                                    {taken}
                                </TableCell>
                                <TableCell align="right">
                                    <Chip
                                        label={`${available} days`}
                                        color={
                                            available < 0
                                                ? "error"
                                                : available > 0
                                                  ? "success"
                                                  : "warning"
                                        }
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<Visibility />}
                                        onClick={() =>
                                            navigate(
                                                `/leave-portal/employee-details/${employee.employee_id}`
                                            )
                                        }
                                    >
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {employeeList.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={9} align="center">
                                No employees found.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Loading screen.
    if (loading) {
        return (
            <>
                <Navbar />
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 6,
                    }}
                >
                    <CircularProgress />
                </Box>
            </>
        );
    }

    // Error screen.
    if (error) {
        return (
            <>
                <Navbar />
                <Box sx={{ p: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 3,
                        }}
                    >
                        <Typography variant="h4" fontWeight={700}>
                            {dashboardTitle}
                        </Typography>

                        <Button
                            color="error"
                            variant="contained"
                            startIcon={<Logout />}
                            onClick={handleLogout}
                        >
                            Logout
                        </Button>
                    </Box>

                    <Alert severity="error">{error}</Alert>

                    <Button
                        sx={{ mt: 2 }}
                        variant="outlined"
                        onClick={() => navigate("/leave-portal")}
                    >
                        Back to Login
                    </Button>
                </Box>
            </>
        );
    }

    // Main dashboard.
    return (
        <>
            <Navbar />

            <Box sx={{ p: { xs: 2, md: 4 }, minWidth: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 1,
                    }}
                >
                    <Typography variant="h4" fontWeight={700}>
                        {dashboardTitle}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            startIcon={<Visibility />}
                            onClick={() =>
                                navigate("/leave-portal/all-employee-leaves")
                            }
                            sx={{
                                bgcolor: "#1565C0",
                                "&:hover": { bgcolor: "#0D47A1" },
                            }}
                        >
                            All Employee Leave Records
                        </Button>

                        {(role === "ADMIN" || role === "REGISTRAR") && (
                            <Button
                                variant="contained"
                                startIcon={<ManageAccountsIcon />}
                                onClick={() =>
                                    navigate("/leave-portal/employee-accounts")
                                }
                                sx={{
                                    bgcolor: "#0B3C5D",
                                    "&:hover": { bgcolor: "#082F49" },
                                }}
                            >
                                View Employee Accounts
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={downloadAllTablesPDF}
                        >
                            Download All Tables PDF
                        </Button>

                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<Logout />}
                            onClick={handleLogout}
                        >
                            Logout
                        </Button>
                    </Box>
                </Box>

                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Rayalaseema University — Leave Management Portal
                </Typography>

                {/* Dashboard statistics */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {stats.map((stat) => (
                        <Grid item xs={12} sm={6} md={3} key={stat.title}>
                            <Card sx={{ height: "100%", borderRadius: 3 }}>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            color: stat.color,
                                            mb: 2,
                                        }}
                                    >
                                        {stat.icon}
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={600}
                                        >
                                            {stat.title}
                                        </Typography>
                                    </Box>

                                    <Typography variant="h4" fontWeight={700}>
                                        {stat.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Employees on Leave Today */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                        Employees on Leave Today
                    </Typography>

                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Date: {todayIST} | Total:{" "}
                        {employeesOnLeaveToday.length}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            width: "100%",
                            mb: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={downloadTodayLeavePDF}
                            sx={{
                                backgroundColor: "#0B3C5D",
                                "&:hover": {
                                    backgroundColor: "#082D46",
                                },
                                px: 3,
                                py: 1,
                            }}
                        >
                            Download PDF
                        </Button>
                    </Box>

                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        Teaching Employees ({todayTeachingEmployees.length})
                    </Typography>

                    <TableContainer
                        component={Paper}
                        sx={{ mb: 3, overflowX: "auto" }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#0B3C5D" }}>
                                    {[
                                        "S.No.",
                                        "Employee Name",
                                        "Employee Code",
                                        "Designation",
                                        "Department",
                                        "College",
                                        "Leave Type",
                                        "Purpose of Leave",
                                        "Leave Dates",
                                        "Days",
                                        "Status",
                                    ].map((heading) => (
                                        <TableCell
                                            key={heading}
                                            sx={{
                                                color: "#fff",
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {heading}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {todayTeachingEmployees.length > 0 ? (
                                    todayTeachingEmployees.map(
                                        (application, index) => (
                                            <TableRow
                                                key={
                                                    application.leave_id ||
                                                    application.application_no ||
                                                    index
                                                }
                                                hover
                                            >
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    {application.employee_name || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {application.employee_code || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {application.designation || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {application.department_name || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {application.college_name || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {application.leave_type || "-"}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 180 }}>
                                                    {application.purpose || "-"}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 130 }}>
                                                    {(application.dates || [])
                                                        .map((date) =>
                                                            String(date).slice(0, 10)
                                                        )
                                                        .join(", ")}
                                                </TableCell>
                                                <TableCell>
                                                    {application.total_days ?? "-"}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={application.status || "-"}
                                                        color="success"
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center">
                                            No teaching employees on leave today.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Non-teaching employees: Registrar and Admin only */}
                    {isRegistrarOrAdmin && (
                        <>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ mb: 1 }}
                            >
                                Non-Teaching Employees (
                                {todayNonTeachingEmployees.length})
                            </Typography>

                            <TableContainer
                                component={Paper}
                                sx={{ overflowX: "auto" }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow
                                            sx={{ backgroundColor: "#0B3C5D" }}
                                        >
                                            {[
                                                "S.No.",
                                                "Employee Name",
                                                "Employee Code",
                                                "Designation",
                                                "Department / Section",
                                                "College",
                                                "Leave Type",
                                                "Purpose of Leave",
                                                "Leave Dates",
                                                "Days",
                                                "Status",
                                            ].map((heading) => (
                                                <TableCell
                                                    key={heading}
                                                    sx={{
                                                        color: "#fff",
                                                        fontWeight: 700,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {heading}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {todayNonTeachingEmployees.length > 0 ? (
                                            todayNonTeachingEmployees.map(
                                                (application, index) => (
                                                    <TableRow
                                                        key={
                                                            application.leave_id ||
                                                            application.application_no ||
                                                            index
                                                        }
                                                        hover
                                                    >
                                                        <TableCell>
                                                            {index + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.employee_name || "-"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.employee_code || "-"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.designation || "-"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.department_name || "-"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.college_name || "-"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.leave_type || "-"}
                                                        </TableCell>
                                                        <TableCell sx={{ minWidth: 180 }}>
                                                            {application.purpose || "-"}
                                                        </TableCell>
                                                        <TableCell sx={{ minWidth: 130 }}>
                                                            {(application.dates || [])
                                                                .map((date) =>
                                                                    String(date).slice(0, 10)
                                                                )
                                                                .join(", ")}
                                                        </TableCell>
                                                        <TableCell>
                                                            {application.total_days ?? "-"}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={
                                                                    application.status || "-"
                                                                }
                                                                color="success"
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            )
                                        ) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={11}
                                                    align="center"
                                                >
                                                    No non-teaching employees on leave today.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>

                {/* Teaching employees */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="h5" fontWeight={700}>
                        Teaching Employees ({filteredTeachingEmployees.length})
                    </Typography>

                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() =>
                            exportTablePDF(
                                "Teaching Employees",
                                employeeHeaders,
                                employeeRows(filteredTeachingEmployees),
                                "Teaching_Employees.pdf"
                            )
                        }
                    >
                        Download Teaching PDF
                    </Button>
                </Box>

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search teaching employees by code, name, designation, department or college..."
                    value={teachingSearch}
                    onChange={(event) => setTeachingSearch(event.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />

                {renderEmployeeTable(filteredTeachingEmployees)}

                {/* Non-teaching employees: Registrar and Admin only */}
                {isRegistrarOrAdmin && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <Typography variant="h5" fontWeight={700}>
                                Non-Teaching Employees (
                                {filteredNonTeachingEmployees.length})
                            </Typography>

                            <Button
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={() =>
                                    exportTablePDF(
                                        "Non-Teaching Employees",
                                        employeeHeaders,
                                        employeeRows(filteredNonTeachingEmployees),
                                        "Non_Teaching_Employees.pdf"
                                    )
                                }
                            >
                                Download Non-Teaching PDF
                            </Button>
                        </Box>

                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search non-teaching employees by code, name, designation, department or college..."
                            value={nonTeachingSearch}
                            onChange={(event) =>
                                setNonTeachingSearch(event.target.value)
                            }
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {renderEmployeeTable(filteredNonTeachingEmployees)}
                    </>
                )}

                {/* Leave applications */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="h5" fontWeight={700}>
                        Leave Applications ({visibleFilteredApplications.length})
                    </Typography>

                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() =>
                            exportTablePDF(
                                "Leave Applications",
                                applicationHeaders,
                                applicationRows(visibleFilteredApplications),
                                "Leave_Applications.pdf"
                            )
                        }
                    >
                        Download Applications PDF
                    </Button>
                </Box>

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search applications by application number, employee, leave type, status or purpose..."
                    value={applicationSearch}
                    onChange={(event) => setApplicationSearch(event.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />

                <TableContainer
                    component={Paper}
                    sx={{ borderRadius: 2, overflowX: "auto", mb: 4 }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F1F5F9" }}>
                                <TableCell>Application No.</TableCell>
                                <TableCell>Employee</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Leave Type</TableCell>
                                <TableCell>Days</TableCell>
                                <TableCell>Selected Dates</TableCell>
                                <TableCell>Purpose</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Submitted On</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {visibleFilteredApplications.map((application) => (
                                <TableRow
                                    key={application.leave_id}
                                    hover
                                >
                                    <TableCell>
                                        {application.application_no || "-"}
                                    </TableCell>
                                    <TableCell>
                                        {application.employee_name ||
                                            application.employee_id ||
                                            "-"}
                                    </TableCell>
                                    <TableCell>
                                        {application.employee_type || "-"}
                                    </TableCell>
                                    <TableCell>
                                        {application.leave_type || "-"}
                                    </TableCell>
                                    <TableCell>
                                        {application.total_days ?? 0}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                            }}
                                        >
                                            {(application.dates || []).map(
                                                (date, index) => (
                                                    <Chip
                                                        key={`${application.leave_id}-${date}-${index}`}
                                                        size="small"
                                                        variant="outlined"
                                                        label={formatDate(date)}
                                                    />
                                                )
                                            )}

                                            {(!application.dates ||
                                                application.dates.length === 0) &&
                                                "-"}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>
                                        {application.purpose || "-"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={
                                                application.status || "UNKNOWN"
                                            }
                                            color={
                                                String(application.status).toUpperCase() ===
                                                "APPROVED"
                                                    ? "success"
                                                    : String(application.status).toUpperCase() ===
                                                        "REJECTED"
                                                      ? "error"
                                                      : "warning"
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {formatSubmittedDate(
                                            application.submitted_at
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {visibleFilteredApplications.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        No leave applications found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Footer />
        </>
    );
}